import { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { useOnboardingPolling } from '@/onboarding/useOnboardingPolling'
import { bookmarkStep, getOnboardingState, rightLabel, type ConnectionState } from '@/api/onboarding'
import { getPollsFlow } from '@/onboarding/pollsFlow'
import { BotPermissionsMock } from '@/polls/BotPermissionsMock'

interface StateMeta {
  tone: 'info' | 'warn' | 'success' | 'error'
  icon: JSX.Element
  headline: string
  body: string
}

const HOURGLASS = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 22h14M5 2h14M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22M17 2v4.172a2 2 0 0 1-.586 1.414L12 12 7.586 7.586A2 2 0 0 1 7 6.172V2" />
  </svg>
)
const SHIELD_OK = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
)
const SHIELD_WARN = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M12 8v4M12 16v.01" />
  </svg>
)
const PERSON_PLUS = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M19 8v6M22 11h-6" />
  </svg>
)

const STATE_META: Record<ConnectionState, StateMeta> = {
  pending: {
    tone: 'info',
    icon: HOURGLASS,
    headline: 'Бот ещё не добавлен в канал',
    body: 'Добавьте его подписчиком — мы это увидим автоматически.',
  },
  bot_subscribed: {
    tone: 'warn',
    icon: PERSON_PLUS,
    headline: 'Бот в подписчиках, но не админ',
    body: 'Откройте настройки канала → раздел «Администраторы» → нажмите «+ Добавить» и выберите бота.',
  },
  bot_admin: {
    tone: 'info',
    icon: SHIELD_WARN,
    headline: 'Бот — админ, проверяем права…',
    body: 'Это занимает пару секунд.',
  },
  rights_partial: {
    tone: 'warn',
    icon: SHIELD_WARN,
    headline: 'Бот — админ, но не хватает части прав',
    body: 'Откройте настройки бота в канале и включите чекбоксы из списка ниже.',
  },
  rights_ok: {
    tone: 'success',
    icon: SHIELD_OK,
    headline: 'Все права на месте',
    body: 'Переходим дальше…',
  },
  expired: {
    tone: 'error',
    icon: HOURGLASS,
    headline: 'Сессия истекла',
    body: 'Возвращаемся к началу…',
  },
}

function relativeTime(ms: number): string {
  if (ms < 1500) return 'только что'
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return `${sec} сек назад`
  const min = Math.floor(sec / 60)
  if (min === 1) return 'минуту назад'
  if (min < 5) return `${min} минуты назад`
  return `${min} минут назад`
}

export function ConnectAdminPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { status, error, polling, forcePoll } = useOnboardingPolling(token || null)

  const [now, setNow] = useState(Date.now())
  const [lastCheckTs, setLastCheckTs] = useState<number | null>(null)
  const [forceResult, setForceResult] = useState<'idle' | 'checking' | 'no-change' | 'changed'>('idle')
  const [wizardRouting, setWizardRouting] = useState(false)
  const prevStateRef = useRef<ConnectionState | undefined>(undefined)
  const flashTimerRef = useRef<number | null>(null)
  const rightsGuideRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    if (status) setLastCheckTs(Date.now())
  }, [status])

  useEffect(() => {
    const prev = prevStateRef.current
    const next = status?.state
    if (next === 'rights_partial' && prev !== 'rights_partial' && window.matchMedia('(max-width: 1000px)').matches) {
      window.setTimeout(() => {
        rightsGuideRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 120)
    }
    prevStateRef.current = next
  }, [status?.state])

  useEffect(() => {
    if (status?.state === 'rights_ok') {
      if (getPollsFlow().active) return
      // Polls-onboarding fast-track: если юзер в polls-flow, минуем connect-done
      // celebration-экран и сразу летим в wizard (Виктор 2026-07-28: connect-done
      // = лишний шаг когда юзер проходит polls-onboarding).
      bookmarkStep('connect-done')
      navigate(`/onboarding/connect-done/${encodeURIComponent(token!)}`, { replace: true })
    }
    if (status?.state === 'expired') {
      const t = setTimeout(() => navigate('/onboarding/source', { replace: true }), 3000)
      return () => clearTimeout(t)
    }
  }, [status?.state, status?.channel?.uuid, token, navigate])

  useEffect(() => {
    if (status?.state !== 'rights_ok') {
      setWizardRouting(false)
      return
    }

    const pollsFlow = getPollsFlow()
    if (!pollsFlow.active) return

    let cancelled = false
    let retryTimer: number | null = null

    const navigateToWizard = (channelUUID: string) => {
      const qs = new URLSearchParams({ channelUUID })
      if (pollsFlow.ref) qs.set('ref', pollsFlow.ref)
      navigate(`/onboarding/polls/template?${qs.toString()}`, { replace: true })
    }

    if (status.channel?.uuid) {
      navigateToWizard(status.channel.uuid)
      return
    }

    setWizardRouting(true)

    const resolveChannelUuid = async () => {
      try {
        const onboarding = await getOnboardingState()
        if (cancelled) return
        if (onboarding.channelUUID) {
          navigateToWizard(onboarding.channelUUID)
          return
        }
      } catch {}
      if (cancelled) return
      retryTimer = window.setTimeout(resolveChannelUuid, 800)
    }

    void resolveChannelUuid()

    const stopRoutingTimer = window.setTimeout(() => {
      if (!cancelled) setWizardRouting(false)
    }, 3200)

    return () => {
      cancelled = true
      window.clearTimeout(stopRoutingTimer)
      if (retryTimer) window.clearTimeout(retryTimer)
    }
  }, [status?.state, status?.channel?.uuid, navigate])

  async function handleRecheck() {
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current)
    setForceResult('checking')
    const before = status?.state
    prevStateRef.current = before
    const after = await forcePoll()
    if (!after) {
      setForceResult('idle')
      return
    }
    if (before !== after.state) {
      setForceResult('changed')
    } else {
      setForceResult('no-change')
    }
    flashTimerRef.current = window.setTimeout(() => setForceResult('idle'), 4000)
  }

  const meta = status?.state ? STATE_META[status.state] : null
  const missing = status?.rights?.missing || []
  const required = status?.rights?.required || []
  const agoMs = lastCheckTs ? now - lastCheckTs : null
  const agoText = agoMs === null ? '—' : relativeTime(agoMs)

  return (
    <OnboardingShell
      step="connect-admin"
      footer={
        <>
          <button className="foot-back" onClick={() => navigate(`/onboarding/connect-subscriber/${encodeURIComponent(token!)}`)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Назад
          </button>
          <div className="foot-spacer" />
          <button
            className={`foot-next ${forceResult === 'changed' ? 'success' : ''}`}
            onClick={handleRecheck}
            disabled={polling}
          >
            {polling ? (
              <>
                <span className="spinner" />
                Проверяем…
              </>
            ) : forceResult === 'changed' ? (
              <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Готово
              </>
            ) : (
              <>
                Проверить ещё раз
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 1 1-9-9c2.5 0 4.7 1 6.4 2.6" />
                  <path d="M21 4v5h-5" />
                </svg>
              </>
            )}
          </button>
        </>
      }
    >
      <p className="stage-eyebrow">
        <span className="stage-eyebrow-pill success">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          Бот&nbsp;в&nbsp;подписчиках
        </span>
      </p>
      <h1 className="stage-title">
        Теперь добавьте&nbsp;бота <em>в&nbsp;администраторы</em>
      </h1>
      <p className="stage-sub">
        Это даёт боту право читать статистику и&nbsp;публиковать посты по&nbsp;вашей команде. Бот <b>не банит, не удаляет посты и не модерирует</b>&nbsp;— стандартных прав достаточно.
      </p>

      {wizardRouting && (
        <div className="wizard-routing-note" role="status">
          Права получены. Открываем настройку опроса…
        </div>
      )}

      <div className={`rights-layout ${status?.state === 'rights_partial' ? 'two-col' : ''}`}>
        <div className="step-card">
        <ul className="step-list">
          <li className="step-row">
            <div className="step-num">1</div>
            <div className="step-content">
              <div className="step-text">Настройки канала&nbsp;→ «Администраторы»</div>
              <div className="step-sub">Тот же экран настроек, раздел <b>«Администраторы»</b>&nbsp;→ «Добавить администратора».</div>
            </div>
          </li>
          <li className="step-row">
            <div className="step-num">2</div>
            <div className="step-content">
              <div className="step-text">Выберите бота из&nbsp;подписчиков</div>
              <div className="step-sub">В списке появится бот, добавленный на предыдущем шаге&nbsp;— нажмите по&nbsp;нему и&nbsp;подтвердите. Стандартных прав достаточно.</div>
            </div>
          </li>
        </ul>

        {meta && (
          <div className={`status-panel tone-${meta.tone} ${forceResult === 'no-change' ? 'flash-attention' : ''}`}>
            <div className="status-icon">{meta.icon}</div>
            <div className="status-content">
              <div className="status-headline">{meta.headline}</div>
              <div className="status-body">{meta.body}</div>

              {status?.state === 'rights_partial' && missing.length > 0 && (
                <div className="rights-chips">
                  {missing.map((key) => {
                    const isRequired = required.includes(key)
                    return (
                      <span key={key} className={`right-chip ${isRequired ? 'req' : 'opt'}`}>
                        <span className="dot" />
                        {rightLabel(key)}
                        <span className="chip-tag">{isRequired ? 'нужно' : 'рекомендуем'}</span>
                      </span>
                    )
                  })}
                </div>
              )}

              <div className="status-meta">
                <span className="auto-pip" />
                Слушаем автоматически · последняя проверка <b>{agoText}</b>
              </div>

              {forceResult === 'no-change' && (
                <div className="force-flash no-change">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8v4M12 16v.01" />
                  </svg>
                  Проверили — пока без изменений. MAX иногда передаёт обновление с задержкой 10–30 секунд. Проверьте, что вы добавили бота именно через раздел <b>«Администраторы»</b>.
                </div>
              )}
              {forceResult === 'changed' && (
                <div className="force-flash changed">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  Статус обновился
                </div>
              )}
            </div>
          </div>
        )}
        </div>

        {status?.state === 'rights_partial' && (
          <div ref={rightsGuideRef}>
            <BotPermissionsMock granted={status.rights?.granted || []} missing={missing} />
          </div>
        )}
      </div>

      {error && <div className="onb-error-inline">Не удалось проверить статус: {error}</div>}

      <style>{`
        .step-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 16px;
          overflow: hidden;
          max-width: 760px;
        }
        .step-list { list-style: none; margin: 0; padding: 0; }
        .step-row {
          display: grid;
          grid-template-columns: 36px 1fr;
          gap: 18px;
          padding: 22px 28px;
          align-items: flex-start;
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
        }
        .step-num {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--tg-blue-bg, #ebf2ff);
          color: var(--tg-blue-press, #0066cc);
          font-weight: 800;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .step-text {
          font-weight: 600;
          font-size: 15px;
          color: var(--tg-ink, #111);
          margin-bottom: 4px;
        }
        .step-sub {
          font-size: 12.5px;
          color: var(--tg-mute, #8f9eb0);
          line-height: 1.45;
        }
        .step-sub b { color: var(--tg-ink-2, #333); font-weight: 600; }

        .status-panel {
          display: grid;
          grid-template-columns: 44px 1fr;
          gap: 16px;
          padding: 20px 28px;
          background: var(--tg-blue-bg, #ebf2ff);
          transition: background 200ms;
          position: relative;
        }
        .status-panel.flash-attention {
          animation: panel-flash 1.2s ease-out;
        }
        .status-panel.flash-attention .status-headline {
          animation: text-flash 1.2s ease-out;
        }
        @keyframes panel-flash {
          0%   { box-shadow: inset 0 0 0 0 rgba(255, 64, 0, 0); }
          12%  { box-shadow: inset 0 0 0 3px rgba(255, 64, 0, 0.85); background: rgba(255, 64, 0, 0.18); }
          40%  { box-shadow: inset 0 0 0 3px rgba(255, 64, 0, 0.5); }
          100% { box-shadow: inset 0 0 0 0 rgba(255, 64, 0, 0); }
        }
        @keyframes text-flash {
          0%, 100% { color: var(--tg-ink, #111); }
          15%, 50% { color: #ff4000; }
        }
        .status-panel.tone-warn { background: rgba(255, 184, 0, 0.10); }
        .status-panel.tone-success { background: rgba(0, 168, 96, 0.10); }
        .status-panel.tone-error { background: rgba(255, 64, 0, 0.08); }
        .status-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: var(--tg-blue-press, #0066cc);
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .status-panel.tone-warn .status-icon { color: rgb(180, 130, 0); }
        .status-panel.tone-success .status-icon { color: var(--tg-up, #00a860); }
        .status-panel.tone-error .status-icon { color: #c33; }
        .status-icon svg { width: 20px; height: 20px; }
        .status-content { min-width: 0; }
        .status-headline {
          font-weight: 700;
          font-size: 14px;
          color: var(--tg-ink, #111);
          margin-bottom: 4px;
        }
        .status-body {
          font-size: 13px;
          line-height: 1.5;
          color: var(--tg-ink-2, #333);
        }
        .status-body b { color: var(--tg-ink, #111); font-weight: 700; }
        .status-meta {
          margin-top: 10px;
          font-size: 12px;
          color: var(--tg-mute, #8f9eb0);
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .status-meta b {
          color: var(--tg-ink-2, #333);
          font-weight: 600;
          font-variant-numeric: tabular-nums;
          display: inline-block;
          min-width: 13ch;
          white-space: nowrap;
        }
        .auto-pip {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--tg-blue, #0080ff);
          animation: tg-pulse-pip 1.6s ease-out infinite;
          flex-shrink: 0;
        }
        @keyframes tg-pulse-pip {
          0% { box-shadow: 0 0 0 0 rgba(0,128,255,0.5); }
          100% { box-shadow: 0 0 0 6px rgba(0,128,255,0); }
        }

        .rights-chips {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
          margin-top: 10px;
        }
        .right-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 10px;
          background: white;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          color: var(--tg-ink-2, #333);
        }
        .right-chip .dot { width: 6px; height: 6px; border-radius: 50%; }
        .right-chip.req .dot { background: #c33; }
        .right-chip.opt .dot { background: var(--tg-mute, #8f9eb0); }
        .chip-tag {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--tg-mute, #8f9eb0);
        }
        .right-chip.req .chip-tag { color: #c33; }

        .force-flash {
          margin-top: 12px;
          padding: 10px 14px;
          border-radius: 10px;
          font-size: 12.5px;
          line-height: 1.5;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          animation: flash-in 200ms ease-out;
        }
        @keyframes flash-in {
          from { transform: translateY(-4px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .force-flash svg { width: 16px; height: 16px; flex-shrink: 0; margin-top: 1px; }
        .force-flash.no-change {
          background: rgba(255, 184, 0, 0.16);
          color: var(--tg-ink-2, #333);
        }
        .force-flash.no-change svg { color: rgb(180, 130, 0); }
        .force-flash.no-change b { color: var(--tg-ink, #111); font-weight: 700; }
        .force-flash.changed {
          background: rgba(0, 168, 96, 0.16);
          color: var(--tg-up, #00a860);
          font-weight: 600;
        }
        .force-flash.changed svg { color: var(--tg-up, #00a860); }

        .foot-next.success {
          background: var(--tg-up, #00a860) !important;
        }
        .foot-next.success:hover {
          background: #00805a !important;
        }
        .spinner {
          width: 12px;
          height: 12px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          flex-shrink: 0;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .onb-error-inline {
          margin-top: 12px;
          padding: 10px 14px;
          background: rgba(255, 64, 0, 0.08);
          border-radius: 8px;
          color: #c33;
          font-size: 12px;
        }
        .wizard-routing-note {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin: 0 0 18px;
          padding: 10px 14px;
          border-radius: 10px;
          background: rgba(0, 128, 255, 0.08);
          color: var(--tg-blue-press, #0066cc);
          font-size: 13px;
          font-weight: 600;
        }
        .wizard-routing-note::before {
          content: "";
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: currentColor;
          animation: tg-pulse-pip 1.6s ease-out infinite;
          flex-shrink: 0;
        }

        /* ── rights_partial: 2-col layout — step-card слева, MAX mock справа ── */
        .rights-layout { display: block; }
        .rights-layout.two-col {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 380px;
          gap: 32px;
          align-items: start;
          max-width: 1160px;
        }
        @media (max-width: 1000px) {
          .rights-layout.two-col { grid-template-columns: 1fr; }
        }

      `}</style>
    </OnboardingShell>
  )
}

